/**
 * Analytics utility for Segment tracking
 *
 * Provides simple functions to track page views, events, and user actions
 * throughout the Landworth Deals Platform
 */

// Check if analytics is available (from Segment snippet)
const isAnalyticsAvailable = () => {
  return (
    typeof window !== "undefined" &&
    window.analytics &&
    typeof window.analytics.track === "function"
  );
};

/**
 * Identify a user (typically called once when app loads)
 * @param {string|null} userId - User ID (optional)
 * @param {object} traits - User traits (optional)
 */
export const identifyUser = (userId = null, traits = {}) => {
  if (!isAnalyticsAvailable()) {
    console.log(
      "Analytics not available - would identify user:",
      userId ? "with ID" : "anonymously",
      traits
    );
    return;
  }

  if (userId) {
    window.analytics.identify(userId, traits);
    console.log("User identified with ID:", userId, traits);
  } else {
    window.analytics.identify();
    console.log("Anonymous user identified");
  }
};

/**
 * Track a page view
 * @param {string} name - Page name
 * @param {object} properties - Additional properties
 */
export const trackPageView = (name, properties = {}) => {
  if (!isAnalyticsAvailable()) {
    console.log(
      "Analytics not available - would track page:",
      name,
      properties
    );
    return;
  }

  window.analytics.page(name, {
    timestamp: new Date(),
    ...properties,
  });

  console.log("Page tracked:", name, properties);
};

/**
 * Track a general event
 * @param {string} event - Event name
 * @param {object} properties - Event properties
 */
export const trackEvent = (event, properties = {}) => {
  if (!isAnalyticsAvailable()) {
    console.log(
      "Analytics not available - would track event:",
      event,
      properties
    );
    return;
  }

  window.analytics.track(event, {
    timestamp: new Date(),
    ...properties,
  });

  console.log("Event tracked:", event, properties);
};

/**
 * Track form submission
 * @param {string} formType - Type of form (deal, project, valuation, etc.)
 * @param {object} data - Form data (will be sanitized)
 */
export const trackFormSubmission = (formType, data = {}) => {
  const sanitizedData = {
    formType,
    fieldCount: Object.keys(data).length,
    hasRequiredFields: Boolean(data.name || data.title),
    timestamp: new Date(),
  };

  trackEvent("Form Submitted", sanitizedData);
};

/**
 * Track item creation
 * @param {string} itemType - Type of item created (deal, project, valuation)
 * @param {object} metadata - Item metadata (sanitized)
 */
export const trackItemCreated = (itemType, metadata = {}) => {
  const sanitizedMetadata = {
    itemType,
    hasTitle: Boolean(metadata.title || metadata.name),
    hasDescription: Boolean(metadata.description),
    timestamp: new Date(),
  };

  trackEvent("Item Created", sanitizedMetadata);
};

/**
 * Track navigation events
 * @param {string} from - Source page/section
 * @param {string} to - Destination page/section
 */
export const trackNavigation = (from, to) => {
  trackEvent("Navigation", {
    from,
    to,
    timestamp: new Date(),
  });
};

/**
 * Track search events
 * @param {string} query - Search query
 * @param {string} section - Section being searched
 * @param {number} resultCount - Number of results (if available)
 */
export const trackSearch = (query, section = "global", resultCount = null) => {
  const properties = {
    query: query ? "has_query" : "empty_query", // Don't track actual query for privacy
    queryLength: query ? query.length : 0,
    section,
    timestamp: new Date(),
  };

  if (resultCount !== null) {
    properties.resultCount = resultCount;
  }

  trackEvent("Search Performed", properties);
};

/**
 * Track filter usage
 * @param {string} filterType - Type of filter applied
 * @param {string} section - Section where filter was applied
 */
export const trackFilter = (filterType, section) => {
  trackEvent("Filter Applied", {
    filterType,
    section,
    timestamp: new Date(),
  });
};

/**
 * Track error events
 * @param {string} errorType - Type of error
 * @param {string} location - Where the error occurred
 * @param {string} message - Error message (sanitized)
 */
export const trackError = (errorType, location, message = "") => {
  trackEvent("Error Occurred", {
    errorType,
    location,
    hasMessage: Boolean(message),
    timestamp: new Date(),
  });
};

// Page-specific tracking helpers
export const PAGE_NAMES = {
  HOME: "Dashboard",
  PROJECTS: "Projects List",
  NEW_PROJECT: "New Project",
  DEALS: "Deals List",
  NEW_DEAL: "New Deal",
  VALUATIONS: "Valuations List",
  NEW_VALUATION: "New Valuation",
  AUTOMATIONS: "Automations",
};

export const ITEM_TYPES = {
  DEAL: "deal",
  PROJECT: "project",
  VALUATION: "valuation",
  AUTOMATION: "automation",
};
